import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, CircularProgress, Alert,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import api from '../../api/axios';

const statusColor = { open: 'warning', in_progress: 'info', resolved: 'success', closed: 'default' };
const priorityColor = { low: 'default', medium: 'warning', high: 'error', urgent: 'error' };

function CreateTicketDialog({ open, onClose, onSuccess }) {
  const [form, setForm] = useState({ subject: '', description: '', priority: 'medium', category: 'general' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handle = async () => {
    if (!form.subject || !form.description) { setError('Subject and description are required.'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/tickets', form);
      setForm({ subject: '', description: '', priority: 'medium', category: 'general' });
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to create ticket');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Support Ticket</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Subject" value={form.subject} onChange={set('subject')} fullWidth required />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField select label="Priority" value={form.priority} onChange={set('priority')} fullWidth>
            {['low', 'medium', 'high', 'urgent'].map(p => <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>)}
          </TextField>
          <TextField select label="Category" value={form.category} onChange={set('category')} fullWidth>
            {['general', 'billing', 'technical', 'feature_request'].map(c => <MenuItem key={c} value={c}>{c.replace('_', ' ')}</MenuItem>)}
          </TextField>
        </Box>
        <TextField label="Description" value={form.description} onChange={set('description')} multiline rows={4} fullWidth required />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handle} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Submit Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tickets');
      setTickets(data.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Support Tickets</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>New Ticket</Button>
      </Box>

      <Card>
        <CardContent>
          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Opened</TableCell>
                  <TableCell>Last Update</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map(t => (
                  <TableRow key={t.id} hover>
                    <TableCell><Typography variant="body2" color="primary" fontWeight={600}>#{t.id}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{t.subject}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.description}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{t.category?.replace('_', ' ')}</Typography></TableCell>
                    <TableCell><Chip label={t.priority} size="small" color={priorityColor[t.priority] || 'default'} /></TableCell>
                    <TableCell><Chip label={t.status?.replace('_', ' ')} size="small" color={statusColor[t.status] || 'default'} /></TableCell>
                    <TableCell><Typography variant="caption">{new Date(t.created_at).toLocaleDateString('en-PK')}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{new Date(t.updated_at).toLocaleDateString('en-PK')}</Typography></TableCell>
                  </TableRow>
                ))}
                {tickets.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No tickets yet. Click "New Ticket" to get help.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateTicketDialog open={open} onClose={() => setOpen(false)} onSuccess={() => { setOpen(false); load(); }} />
    </Box>
  );
}
